import { Button, Typography } from '@recruit-robin/robin-theme/web-components';
import { Check, Clipboard, RobinLogo, XClose } from '@recruit-robin/robin-theme/web-icons';
import { useContext } from 'react';

import { PoweredByRobin } from 'shared/ui/powered-by-robin';
import { TenantLogo } from 'shared/ui/tenant-logo';

import type { VacancyDetailsProps } from './VacancyDetails.types';

import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { ConfirmationStatus } from 'views/LoggedOut/RJCommHubCandidateConfirmation/RJCommHubCandidateConfirmation.types';
import { useStyles } from './VacancyDetails.styles';
import { VacancyDetailsContent } from './VacancyDetailsContent';

export const VacancyDetails = ({
  vacancy,
  isLoading,
  error,
  onSubmit,
  onUnsubscribe,
  candidateAction,
  questionnaireUrl,
  onQuestionnaireClick,
  readonly = false,
}: VacancyDetailsProps) => {
  const { dictionary } = useContext(LocalizationContext);
  const classes = useStyles({ readonly });
  const primaryColorCss = TenantLogo.useColorsCss(vacancy?.branding);
  const resolvedCandidateAction = candidateAction ?? vacancy?.candidate_action ?? 'interest_response';
  const resolvedQuestionnaireUrl = questionnaireUrl ?? vacancy?.questionnaire_url ?? null;
  const handleQuestionnaireClick = onQuestionnaireClick ?? ((url: string) => window.location.assign(url));

  if (isLoading) {
    return (
      <div css={classes.root}>
        <Typography variant={(v) => v.body.long} color={(c) => c.neutral.variant[70]}>
          {dictionary.loadingVacancyDetails}
        </Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div css={classes.root}>
        <Typography variant={(v) => v.body.long} color={(c) => c.tertiary.red[50]}>
          {dictionary.errorLoadingVacancy(error)}
        </Typography>
      </div>
    );
  }

  if (!vacancy) {
    return (
      <div css={classes.root}>
        <Typography variant={(v) => v.body.long} color={(c) => c.neutral.variant[70]}>
          {dictionary.vacancyNotFound}
        </Typography>
      </div>
    );
  }

  return (
    <div css={classes.root}>
      <div css={classes.header}>
        <TenantLogo branding={vacancy.branding} fallback={<RobinLogo height={32} />} />
        <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[80]}>
          {vacancy.job.job_details.job_title || ''}
        </Typography>
        {vacancy.job.tenant_name && (
          <Typography variant={(v) => v.heading[6]} color={(c) => c.neutral.variant[80]}>
            {vacancy.job.tenant_name}
          </Typography>
        )}
      </div>

      <VacancyDetailsContent vacancy={vacancy} readonly={readonly} />

      {!readonly && resolvedCandidateAction === 'questionnaire' && resolvedQuestionnaireUrl && (
        <div css={classes.actionsWrapper}>
          <Button
            css={classes.button}
            type="highlight-light"
            style="filled"
            startIcon={<Clipboard size={18} />}
            label={dictionary.showQuestionnaire}
            onClick={() => handleQuestionnaireClick(resolvedQuestionnaireUrl)}
            customCss={primaryColorCss}
          />
        </div>
      )}

      {!readonly && resolvedCandidateAction === 'interest_response' && (
        <div css={classes.actionsWrapper}>
          <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
            {dictionary.interestedInOpportunity}
          </Typography>
          <div css={classes.buttons}>
            <>
              <Button
                css={classes.button}
                type="highlight-light"
                style="filled"
                startIcon={<Check size={18} />}
                label={dictionary.yesPlease}
                onClick={() => onSubmit(ConfirmationStatus.Positive)}
                customCss={primaryColorCss}
              />
              <Button
                css={classes.button}
                type="subtle2"
                style="text"
                startIcon={<XClose size={18} />}
                label={dictionary.noThanks}
                onClick={() => onSubmit(ConfirmationStatus.Negative)}
              />
            </>
          </div>
        </div>
      )}

      <div css={classes.divider} />

      <div css={classes.footer}>
        <Typography variant={(v) => v.supporting.helper} color={(c) => c.neutral.variant[50]}>
          {dictionary.questionsEmailUs}
        </Typography>
        <a href="mailto:support@recruitrobin.com" style={{ textDecoration: 'none' }}>
          <Typography variant={(v) => v.supporting.helper} color={(c) => c.secondary.sky[50]}>
            support@recruitrobin.com
          </Typography>
        </a>
      </div>

      <PoweredByRobin onUnsubscribe={onUnsubscribe} isEmail={false} />
    </div>
  );
};
